
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import SearchHeader from "@/components/search/SearchHeader";
import SearchResults from "@/components/search/SearchResults";
import { getCategories } from "@/services/promptService";
import { useEnhancedSearch } from "@/hooks/useEnhancedSearch";
import type { Category } from "@/services/promptService";

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") || "";
  const initialCategory = searchParams.get("category") || "";
  
  const [categories, setCategories] = useState<Category[]>([]);
  
  // Use enhanced search hook
  const {
    searchQuery,
    filters,
    results,
    suggestions,
    searchHistory,
    isLoading,
    isLoadingSuggestions,
    handleSearchChange,
    handleFilterChange,
    clearSearch,
    resultCount
  } = useEnhancedSearch({
    initialQuery,
    initialFilters: { 
      categoryFilter: initialCategory,
      sortBy: 'relevance'
    },
    debounceMs: 300,
    autoSearch: true
  });

  useEffect(() => {
    const loadCategories = async () => {
      const cats = await getCategories();
      setCategories(cats);
    };
    
    loadCategories();
  }, []);

  // Update URL when search query or filters change
  useEffect(() => {
    const params: Record<string, string> = {};
    if (searchQuery) params.q = searchQuery;
    if (filters.categoryFilter) params.category = filters.categoryFilter;
    setSearchParams(params);
  }, [searchQuery, filters.categoryFilter, setSearchParams]);

  // Update category filter when URL parameter changes
  useEffect(() => {
    const categoryParam = searchParams.get("category") || "";
    if (categoryParam !== filters.categoryFilter) {
      handleFilterChange({ categoryFilter: categoryParam });
    }
  }, [searchParams, filters.categoryFilter, handleFilterChange]);

  const handleSearch = (query: string) => {
    handleSearchChange(query);
  };

  const handleCategoryFilter = (categoryId: string) => {
    if (filters.categoryFilter === categoryId) {
      // Deselect if already selected
      handleFilterChange({ categoryFilter: "" });
      const params = new URLSearchParams(searchParams);
      params.delete("category");
      setSearchParams(params);
    } else {
      handleFilterChange({ categoryFilter: categoryId });
      setSearchParams({ 
        ...(searchQuery ? { q: searchQuery } : {}), 
        category: categoryId 
      });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-6xl">
          <SearchHeader 
            onSearch={handleSearch}
            searchQuery={searchQuery}
            categories={categories}
            selectedCategory={filters.categoryFilter || ""}
            onCategoryFilter={handleCategoryFilter}
            suggestions={suggestions}
            searchHistory={searchHistory}
            isLoadingSuggestions={isLoadingSuggestions}
            isSearching={isLoading}
            resultsCount={resultCount}
          />
          
          <SearchResults 
            isLoading={isLoading}
            results={results}
            searchQuery={searchQuery}
            hasCategory={!!filters.categoryFilter}
          />
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default SearchPage;
